import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  FaDatabase,
  FaChartBar,
  FaChartPie,
  FaCog,
  FaBell,
  FaChevronRight,
  FaChevronDown,
} from "react-icons/fa";
import {
  MentionableEntity,
  EntityType,
  EntityMentionState,
  getEntityTypeLabel,
} from "@/hooks/use-entity-mentions";

// ============================================================================
// Types
// ============================================================================

interface EntityMentionPopupProps {
  /** Current mention state */
  mentionState: EntityMentionState;
  /** Filtered entities to display */
  entities: MentionableEntity[];
  /** Whether data is loading */
  isLoading?: boolean;
  /** Callback when an entity is selected */
  onSelect: (entity: MentionableEntity) => void;
  /** Callback to close the popup */
  onClose: () => void;
  /** Callback to update highlighted index (deprecated - navigation is now internal) */
  onHighlightChange?: (index: number) => void;
}

// Navigation item type - can be a type header or an entity
type NavItem = 
  | { kind: "header"; type: EntityType }
  | { kind: "entity"; entity: MentionableEntity; globalIndex: number };

// ============================================================================
// Constants
// ============================================================================

const ENTITY_ICONS: Record<EntityType, React.ReactNode> = {
  tracker: <FaDatabase className="h-3.5 w-3.5" />,
  dashboard: <FaChartBar className="h-3.5 w-3.5" />,
  widget: <FaChartPie className="h-3.5 w-3.5" />,
  enrichment: <FaCog className="h-3.5 w-3.5" />,
  alert: <FaBell className="h-3.5 w-3.5" />,
};

const ENTITY_COLORS: Record<EntityType, string> = {
  tracker: "text-blue-400 bg-blue-500/10",
  dashboard: "text-purple-400 bg-purple-500/10",
  widget: "text-green-400 bg-green-500/10",
  enrichment: "text-orange-400 bg-orange-500/10",
  alert: "text-red-400 bg-red-500/10",
};

// ============================================================================
// Component
// ============================================================================

export const EntityMentionPopup: React.FC<EntityMentionPopupProps> = ({
  mentionState,
  entities,
  isLoading = false,
  onSelect,
  onClose,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onHighlightChange: _onHighlightChange, // Deprecated - navigation is now internal
}) => {
  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  
  // Track collapsed state for each entity type - all collapsed by default
  const [collapsedTypes, setCollapsedTypes] = useState<Set<EntityType>>(
    new Set<EntityType>(["tracker", "dashboard", "widget", "enrichment", "alert"])
  );
  
  // Track the currently highlighted nav item index (includes headers and entities)
  const [highlightedNavIndex, setHighlightedNavIndex] = useState(0);
  
  // Toggle collapse state for a type
  const toggleTypeCollapse = (type: EntityType) => {
    setCollapsedTypes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(type)) {
        newSet.delete(type);
      } else {
        newSet.add(type);
      }
      return newSet;
    });
  };
  
  // Expand a type
  const expandType = (type: EntityType) => {
    setCollapsedTypes((prev) => {
      const newSet = new Set(prev);
      newSet.delete(type);
      return newSet;
    });
  };
  
  // Collapse a type
  const collapseType = (type: EntityType) => {
    setCollapsedTypes((prev) => {
      const newSet = new Set(prev);
      newSet.add(type);
      return newSet;
    });
  };

  // Group entities by type for better organization
  const groupedEntities = entities.reduce(
    (acc, entity) => {
      if (!acc[entity.type]) {
        acc[entity.type] = [];
      }
      acc[entity.type].push(entity);
      return acc;
    },
    {} as Record<EntityType, MentionableEntity[]>
  );

  // Flatten for index tracking
  const flatEntities = entities;

  // Define type order for hierarchical display
  const typeOrder: EntityType[] = ["tracker", "dashboard", "widget", "enrichment", "alert"];
  
  // Sort grouped entities by type order
  const sortedGroupedEntities = typeOrder
    .filter((type) => groupedEntities[type]?.length > 0)
    .map((type) => [type, groupedEntities[type]] as [EntityType, MentionableEntity[]]);
  
  // Build navigation items list (headers + visible entities)
  const navItems: NavItem[] = [];
  sortedGroupedEntities.forEach(([type, typeEntities]) => {
    // Add header
    navItems.push({ kind: "header", type });
    // Add entities only if not collapsed
    if (!collapsedTypes.has(type)) {
      typeEntities.forEach((entity) => {
        const globalIndex = flatEntities.findIndex(
          (e) => e.id === entity.id && e.type === entity.type
        );
        navItems.push({ kind: "entity", entity, globalIndex });
      });
    }
  });

  // Get current highlighted item
  const currentNavItem = navItems[highlightedNavIndex];

  // Scroll highlighted item into view
  useEffect(() => {
    const item = navItems[highlightedNavIndex];
    if (item) {
      const key = item.kind === "header" ? `header-${item.type}` : `entity-${item.globalIndex}`;
      const el = itemRefs.current.get(key);
      if (el && listRef.current) {
        el.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }
  }, [highlightedNavIndex, navItems]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!mentionState.isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightedNavIndex((prev) => Math.min(prev + 1, navItems.length - 1));
          break;
        case "ArrowUp":
          e.preventDefault();
          setHighlightedNavIndex((prev) => Math.max(prev - 1, 0));
          break;
        case "ArrowRight":
          e.preventDefault();
          // Expand if on a header
          if (currentNavItem?.kind === "header") {
            expandType(currentNavItem.type);
          }
          break;
        case "ArrowLeft":
          e.preventDefault();
          // Collapse if on a header, or go to parent header if on entity
          if (currentNavItem?.kind === "header") {
            collapseType(currentNavItem.type);
          } else if (currentNavItem?.kind === "entity") {
            // Find the header for this entity and collapse it
            const entityType = currentNavItem.entity.type;
            collapseType(entityType);
            // Move highlight to the header
            const headerIndex = navItems.findIndex(
              (item) => item.kind === "header" && item.type === entityType
            );
            if (headerIndex >= 0) {
              setHighlightedNavIndex(headerIndex);
            }
          }
          break;
        case "Enter":
        case "Tab":
          e.preventDefault();
          if (currentNavItem?.kind === "header") {
            // Toggle collapse on header
            toggleTypeCollapse(currentNavItem.type);
          } else if (currentNavItem?.kind === "entity") {
            // Select entity
            onSelect(currentNavItem.entity);
          }
          break;
        case "Escape":
          e.preventDefault();
          onClose();
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [mentionState.isOpen, navItems, currentNavItem, onSelect, onClose]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (listRef.current && !listRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  // Reset highlight when popup opens
  useEffect(() => {
    if (mentionState.isOpen) {
      setHighlightedNavIndex(0);
    }
  }, [mentionState.isOpen]);

  if (!mentionState.isOpen) {
    return null;
  }

  // Calculate position to display above the input
  // bottom is calculated as: viewport height - input top position + gap
  const popupStyle: React.CSSProperties = {
    bottom: `calc(100vh - ${mentionState.position.top}px + 8px)`,
    left: Math.max(8, mentionState.position.left), // Ensure minimum 8px from left edge
  };

  const content = (
    <div
      ref={listRef}
      className="fixed z-[9999] w-72 max-h-80 rounded-xl border border-[var(--arc-sidebar-border-accent)] bg-[var(--arc-sidebar-background)] shadow-[0_8px_32px_rgba(0,0,0,0.4)] backdrop-blur-xl flex flex-col"
      style={popupStyle}
    >
      {/* Header - fixed at top */}
      <div className="flex-shrink-0 px-3 py-2 border-b border-[var(--arc-sidebar-border-accent)] bg-[var(--arc-sidebar-background)] rounded-t-xl">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-[var(--arc-sidebar-text-secondary)]">
            Mentionner un élément
          </span>
          {mentionState.query && (
            <span className="text-xs text-[var(--arc-content-tertiary)]">
              "{mentionState.query}"
            </span>
          )}
        </div>
      </div>

      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {/* Loading state */}
        {isLoading && (
          <div className="px-3 py-4 text-center">
            <div className="flex items-center justify-center gap-2 text-xs text-[var(--arc-sidebar-text-secondary)]">
              <div className="h-3 w-3 animate-spin rounded-full border-2 border-accent border-t-transparent" />
              Chargement...
            </div>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && flatEntities.length === 0 && (
          <div className="px-3 py-4 text-center">
            <p className="text-xs text-[var(--arc-content-tertiary)]">
              {mentionState.query
                ? `Aucun élément trouvé pour "${mentionState.query}"`
                : "Aucun élément disponible"}
            </p>
          </div>
        )}

        {/* Entity list grouped by type - hierarchical display with collapse */}
        {!isLoading && flatEntities.length > 0 && (
          <div className="py-1">
            {sortedGroupedEntities.map(([type, typeEntities]) => {
              const isCollapsed = collapsedTypes.has(type);
              const headerNavIndex = navItems.findIndex(
                (item) => item.kind === "header" && item.type === type
              );
              const isHeaderHighlighted = highlightedNavIndex === headerNavIndex;
              
              return (
                <div key={type} className="mb-0.5">
                  {/* Type header with collapse toggle */}
                  <div
                    ref={(el) => {
                      if (el) {
                        itemRefs.current.set(`header-${type}`, el);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleTypeCollapse(type)}
                    onMouseEnter={() => setHighlightedNavIndex(headerNavIndex)}
                    className={`
                      w-full px-3 py-1.5 flex items-center gap-2 transition-colors cursor-pointer
                      ${
                        isHeaderHighlighted
                          ? "bg-[rgba(var(--arc-accent-rgb),0.3)] ring-1 ring-[var(--arc-accent)] ring-inset"
                          : "bg-[var(--arc-sidebar-gradient-start)]/30 hover:bg-[var(--arc-sidebar-gradient-start)]/50"
                      }
                    `}
                  >
                    {/* Collapse/Expand chevron */}
                    <div className={`transition-transform duration-150 ${isHeaderHighlighted ? "text-accent" : "text-[var(--arc-content-tertiary)]"}`}>
                      {isCollapsed ? (
                        <FaChevronRight className="h-2.5 w-2.5" />
                      ) : (
                        <FaChevronDown className="h-2.5 w-2.5" />
                      )}
                    </div>
                    
                    {/* Type icon */}
                    <div className={`${ENTITY_COLORS[type]} p-1 rounded ${isHeaderHighlighted ? "ring-1 ring-white/30" : ""}`}>
                      {ENTITY_ICONS[type]}
                    </div>
                    
                    {/* Type label and count */}
                    <span className={`text-[10px] font-semibold uppercase tracking-wider flex-1 text-left ${isHeaderHighlighted ? "text-white" : "text-[var(--arc-content-tertiary)]"}`}>
                      {getEntityTypeLabel(type)}
                    </span>
                    <span className={`text-[10px] tabular-nums ${isHeaderHighlighted ? "text-white/80" : "text-[var(--arc-content-tertiary)]"}`}>
                      {typeEntities.length}
                    </span>
                  </div>

                  {/* Entities of this type - collapsible */}
                  {!isCollapsed && (
                    <div className="overflow-hidden">
                      {typeEntities.map((entity) => {
                        const globalIndex = flatEntities.findIndex(
                          (e) => e.id === entity.id && e.type === entity.type
                        );
                        const entityNavIndex = navItems.findIndex(
                          (item) => item.kind === "entity" && item.globalIndex === globalIndex
                        );
                        const isEntityHighlighted = highlightedNavIndex === entityNavIndex;

                        return (
                          <div
                            key={`${entity.type}-${entity.id}`}
                            ref={(el) => {
                              if (el) {
                                itemRefs.current.set(`entity-${globalIndex}`, el);
                              }
                            }}
                            className={`
                              flex items-center gap-2.5 pl-8 pr-3 py-2 cursor-pointer transition-colors
                              ${
                                isEntityHighlighted
                                  ? "bg-[rgba(var(--arc-accent-rgb),0.3)] text-white ring-1 ring-[var(--arc-accent)] ring-inset"
                                  : "text-[var(--arc-sidebar-text-secondary)] hover:bg-[var(--arc-sidebar-gradient-middle)]"
                              }
                            `}
                            onClick={() => onSelect(entity)}
                            onMouseEnter={() => setHighlightedNavIndex(entityNavIndex)}
                          >
                            {/* Hierarchy indicator */}
                            <div className="w-1.5 h-1.5 rounded-full bg-[var(--arc-sidebar-border-accent)] flex-shrink-0" />

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate">
                                {entity.name}
                              </div>
                              {entity.description && (
                                <div className="text-[10px] text-[var(--arc-content-tertiary)] truncate">
                                  {entity.description}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer hint - fixed at bottom */}
      <div className="flex-shrink-0 px-3 py-1.5 border-t border-[var(--arc-sidebar-border-accent)] bg-[var(--arc-sidebar-background)] rounded-b-xl">
        <div className="flex items-center justify-between text-[10px] text-[var(--arc-content-tertiary)]">
          <div className="flex items-center gap-2">
            <span>
              <kbd className="px-1 py-0.5 rounded bg-[var(--arc-sidebar-gradient-middle)] text-[var(--arc-sidebar-text-secondary)]">
                ↑↓
              </kbd>{" "}
              nav
            </span>
            <span>
              <kbd className="px-1 py-0.5 rounded bg-[var(--arc-sidebar-gradient-middle)] text-[var(--arc-sidebar-text-secondary)]">
                ←→
              </kbd>{" "}
              ±
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span>
              <kbd className="px-1 py-0.5 rounded bg-[var(--arc-sidebar-gradient-middle)] text-[var(--arc-sidebar-text-secondary)]">
                ↵
              </kbd>{" "}
              ok
            </span>
            <span>
              <kbd className="px-1 py-0.5 rounded bg-[var(--arc-sidebar-gradient-middle)] text-[var(--arc-sidebar-text-secondary)]">
                esc
              </kbd>
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  // Use portal to render at document body level
  return createPortal(content, document.body);
};

export default EntityMentionPopup;
